const PIPELINE_CONFIG_ID_PREFIX = 'pipeline-config-'

/**
 * Helpers for the RETIRED `<bareDocId>_<configSuffix>` id convention.
 * Scoping is tag-based (bare ids + `pipeline-config` tags); these exist only
 * so stale suffixed ids from old links and persisted tabs can be recognized
 * and reduced to their bare form. Do not use them in resolution paths.
 */

/** The id fragment a pipeline config contributed to old scoped document ids. */
export const pipelineConfigSuffix = (pipelineConfigId: string): string =>
  pipelineConfigId.startsWith(PIPELINE_CONFIG_ID_PREFIX)
    ? pipelineConfigId.slice(PIPELINE_CONFIG_ID_PREFIX.length)
    : pipelineConfigId

/**
 * Drop a *known* config's `_<suffix>` tail if the id carries it; to fully
 * un-scope an id of unknown origin, fold this over the site's configs.
 */
export const unscopeDocumentIdFromPipelineConfig = (
  documentId: string,
  pipelineConfigId: string | undefined
): string => {
  if (!pipelineConfigId) return documentId
  const tail = `_${pipelineConfigSuffix(pipelineConfigId)}`
  return documentId.endsWith(tail)
    ? documentId.slice(0, -tail.length)
    : documentId
}

/** Prod search config suffixes are the 12-hex pipeline-config id tail. */
const LEGACY_SEARCH_CONFIG_SUFFIX = /^(DOC-[0-9a-f]+)_[0-9a-f]{12}$/i

/**
 * TEMPORARY FE bridge until power-backend #161 ships bare search ids.
 * Unscope against the selected pipeline-config when present; otherwise strip a
 * trailing `_<12 hex>` from `DOC-*` ids so legacy/no-active-config sites
 * (e.g. Hickory Run) can still hydrate via `data.document_id`.
 */
export const unscopeSearchDocumentIdForHydration = (
  documentId: string,
  pipelineConfigId: string | undefined
): string => {
  const fromConfig = unscopeDocumentIdFromPipelineConfig(
    documentId,
    pipelineConfigId
  )
  if (fromConfig !== documentId) return fromConfig
  const match = LEGACY_SEARCH_CONFIG_SUFFIX.exec(documentId)
  return match?.[1] ?? documentId
}

/**
 * Strip a legacy config-scoped search page_id (`{doc}_{suffix}_pg_{n}`) back
 * to `{doc}_pg_{n}`.
 */
export function unscopePageIdFromPipelineConfig(
  pageId: string,
  pipelineConfigId?: string
): string {
  const separatorIndex = pageId.lastIndexOf('_pg_')
  if (separatorIndex <= 0) {
    return unscopeSearchDocumentIdForHydration(pageId, pipelineConfigId)
  }
  const documentId = pageId.slice(0, separatorIndex)
  const pageSuffix = pageId.slice(separatorIndex)
  return (
    unscopeSearchDocumentIdForHydration(documentId, pipelineConfigId) +
    pageSuffix
  )
}
